import {
  ColumnDef, ColumnPinningState, createColumnHelper, getCoreRowModel, getPaginationRowModel, getSortedRowModel, SortingState, useReactTable
} from '@tanstack/react-table';
import clsx from 'clsx';
import React, { useEffect, useMemo, useState } from 'react';
import { AiFillEye, AiOutlineInfoCircle } from 'react-icons/ai';
import { FiRefreshCw } from 'react-icons/fi';
import { GoAlertFill } from 'react-icons/go';
import { PiCaretCircleDownBold, PiCaretCircleUpBold } from 'react-icons/pi';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast,ToastContainer } from 'react-toastify';
import { match } from 'ts-pattern';
import 'react-toastify/dist/ReactToastify.css';

import APIGateway from '@/api';
import { SummaryCard } from '@/app/evaluations/_components/SummaryCard';
import ModalExampleShow from '@/app/evaluations/_modalExampleShow/modalExampleShow';
import ModalMetricShow from '@/app/evaluations/_modalMetricShow/modalMetricShow';
import ModalRunRerun from '@/app/evaluations/_modalRunRerun/modalRunRerun';
import ModalSourceShow from '@/app/evaluations/_modalSourceShow/modalSourceShow';
import { Badge } from '@/components/badge/badge';
import { Button } from '@/components/button/button';
import { DetailPageHeader } from '@/components/detailPageHeader/detailPageHeader';
import { Icon } from '@/components/icon/icon';
import { Loader } from '@/components/loader/loader';
import { NativeTable } from '@/components/nativeTable/nativeTable';
import { StatusIcon } from '@/components/statusIcon/statusIcon';
import { Tooltip } from '@/components/tooltip/tooltip';
import { Heading } from '@/components/typography/heading';
import { useEvaluationRunResults, useSchedulesList } from '@/hooks/queries/useEvaluations';
import { useOrgUsersMapQuery } from '@/hooks/queries/useOrg';
import { useLocalZonePreference } from '@/hooks/useLocalZonePreference';
import { useRunProgress } from '@/hooks/useRunProgress';
import {
  DetailMetricInfo, Evaluation, MetricConfig, MetricDataMap, SOURCE_TYPE_DISPLAY_NAMES, TERMINAL_RUN_STATUSES
} from '@/types/evals';
import { formatCronCadence } from '@/utils/cronFormat';
import { formatScore } from '@/utils/numberUtils';
import { formatRunCreateError, parseRunCreateError } from '@/utils/runCreateError';
import { parseParams } from '@/utils/searchParams';
import { truncate } from '@/utils/stringUtils';
import { OutputFormatEnum, TimeFormatter, TimeSourceTypeEnum } from '@/utils/timeFormatter';
import { getUserDisplayName } from '@/utils/userDisplay';

import styles from './page.module.scss';


const columnHelper = createColumnHelper<Record<string, unknown>>();

const ERROR_RESULT_STATUSES = [ 'failed', 'error' ];

// Friendly label shown next to the run status icon ('completed' → "Success",
// 'failed' → "Error"); unknown statuses fall back to a capitalized form.
const RUN_STATUS_LABELS: Record<string, string> = {
  complete: 'Success',
  completed: 'Success',
  failed: 'Error',
  cancelled: 'Cancelled',
  incomplete: 'Incomplete',
  queued: 'Queued',
  running: 'Running'
};

function runStatusLabel( status?: string ): string {
  if( !status ) return '';
  const key = status.toLowerCase();
  return RUN_STATUS_LABELS[ key ] ?? ( status.charAt( 0 ).toUpperCase() + status.slice( 1 ).toLowerCase() );
}

// Resolve the version number a run's evaluation pins for a given metric provider.
function resolveMetricVersion(
  evaluation: Evaluation | undefined,
  metricProviderVersions: Record<string, Record<string, number>>,
  providerId: string
): number | undefined {
  const versionId = evaluation?.metric_providers.find( mp => mp.metric_provider_id === providerId )?.version_id;
  return versionId ? metricProviderVersions[ providerId ]?.[ versionId ] : undefined;
}

// Build the metric detail-page link (undefined when the version is unknown).
function metricPageLink( providerId: string, versionNumber?: number ): string | undefined {
  return versionNumber
    ? `/evaluations/metric?metric_provider_id=${ encodeURIComponent( providerId ) }&version=${ versionNumber }`
    : undefined;
}

type SearchParams = {
  eid: string;
  rid: string;
};


function StatSummary( { metric, providerName, versionNumber, onShowMetricModal }: {
  metric: DetailMetricInfo;
  providerName?: string;
  versionNumber?: number;
  onShowMetricModal: ( config: MetricConfig ) => void;
} ) {
  const _handlePreview = async() => {
    if( !versionNumber ) return;
    try {
      const res = await APIGateway.evals.metricProviderVersionGet( metric.metric_provider_id, versionNumber );
      onShowMetricModal( res );
    } catch( e ) {
      console.error( e );
      toast.error( 'Unable to load metric configuration' );
    }
  };

  const metricLink = metricPageLink( metric.metric_provider_id, versionNumber );

  return (
    <div className={ clsx( styles.stat_card, metricLink && styles.stat_card_clickable ) } data-testid="metric-stat-card">
      <div className={ styles.stat_header }>
        <span className={ styles.stat_name }>
          { metricLink
            ? <Link to={ metricLink } className={ styles.stat_link }>{ metric.metric_name }</Link>
            : metric.metric_name }
        </span>
        <div className={ styles.stat_actions }>
          { versionNumber && (
            <Tooltip content="Preview metric" position="top">
              <button
                type="button"
                className={ styles.icon_button }
                aria-label={ `Preview metric: ${ metric.metric_name }` }
                onClick={ _handlePreview }
              >
                <AiFillEye aria-hidden="true" />
              </button>
            </Tooltip>
          ) }
        </div>
      </div>
      <div className={ styles.stat_average }>{ formatScore( metric.average_score ) }</div>
      { providerName && <div className={ styles.stat_provider }>{ providerName }</div> }
    </div>
  );
}

function Stats( { sMetricKeys, sMetricDataMap, evaluation, metricProviderNames, metricProviderVersions, onShowMetricModal }: {
  sMetricKeys: string[];
  sMetricDataMap: MetricDataMap;
  evaluation?: Evaluation;
  metricProviderNames: Record<string, string>;
  metricProviderVersions: Record<string, Record<string, number>>;
  onShowMetricModal: ( config: MetricConfig ) => void;
} ) {
  return sMetricKeys.map( ( metricKey, i ) => {
    const metric = sMetricDataMap[ metricKey ];
    const providerName = metricProviderNames[ metric.metric_provider_id ] || metric.metric_provider_id;
    const versionNumber = resolveMetricVersion( evaluation, metricProviderVersions, metric.metric_provider_id );

    return (
      <StatSummary
        key={ i }
        metric={ metric }
        providerName={ providerName }
        versionNumber={ versionNumber }
        onShowMetricModal={ onShowMetricModal }
      />
    );
  } );
}

export default function EvaluationResultsPage() {
  const [ searchParams ] = useSearchParams();
  const {
    eid: qEvaluationId = '',
    rid: qRunId = ''
  } = parseParams<SearchParams>( searchParams );
  const navigate = useNavigate();

  const { data, isLoading: sIsLoading, error, refetch: refetchResults } = useEvaluationRunResults( qEvaluationId, qRunId );
  const { data: usersMap } = useOrgUsersMapQuery();
  const [ useLocalZone ] = useLocalZonePreference();
  const scheduleId = data?.run?.run.schedule_id;
  const { data: schedules } = useSchedulesList(
    { evaluation_id: qEvaluationId, include_details: false, limit: 500 },
    { enabled: !!scheduleId }
  );
  const runSchedule = useMemo( () => schedules?.items?.find( s => s.schedule.id === scheduleId )?.schedule, [ schedules, scheduleId ] );

  // Stream progress for in-progress runs
  const runStatus = data?.run?.run.status;
  const isRunActive = !!runStatus && !( TERMINAL_RUN_STATUSES as readonly string[] ).includes( runStatus );
  const { progress: runProgress, isTerminal: runIsTerminal } = useRunProgress( qRunId || undefined, isRunActive );

  useEffect( () => {
    if( runIsTerminal ) {
      refetchResults();
    }
  }, [ runIsTerminal, refetchResults ] );

  const [ sShowSourceModal, setSShowSourceModal ] = useState<boolean>( false );
  const [ sShowMetricModal, setSShowMetricModal ] = useState<boolean>( false );
  const [ sSelectedMetricConfig, setSSelectedMetricConfig ] = useState<MetricConfig | undefined>();
  const [ sShowRerunModal, setSShowRerunModal ] = useState<boolean>( false );
  const [ sIsRerunning, setSIsRerunning ] = useState<boolean>( false );
  const [ sorting, setSorting ] = useState<SortingState>( [] );
  const [ columnPinning ] = useState<ColumnPinningState>( { left: [ 'conversationId' ] } );
  const [ sExpandedRows, setSExpandedRows ] = useState<Set<string>>( new Set() );
  const [ sSelectedExampleId, setSSelectedExampleId ] = useState<string | null>( null );
  const [ sShowExampleModal, setSShowExampleModal ] = useState<boolean>( false );

  // Extract data from query result
  const sEvaluation = data?.evaluation;
  const sRun = data?.run;
  const sMetricDataMap = data?.metricDataMap ?? {};
  const sMetricKeys = data?.metricKeys ?? [];
  const sExampleMetricDataMap = data?.exampleMetricDataMap ?? {};
  const sSource = data?.source;
  const sSourceName = data?.sourceName;
  const sMetricProviderNames = data?.metricProviderNames ?? {};
  const sMetricProviderVersions = data?.metricProviderVersions ?? {};

  const sRunBy = getUserDisplayName( usersMap, sRun?.run.created_by );

  // Show error toast if query fails
  useEffect( () => {
    if ( error ) {
      toast.error( 'Unable to get evaluations' );
    }
  }, [ error ] );

  /* HELPERS */
  const _handleToggleRow = ( exampleId: string ) => {
    setSExpandedRows( prev => {
      const next = new Set( prev );
      if( next.has( exampleId ) ) { next.delete( exampleId ); } else { next.add( exampleId ); }
      return next;
    } );
  };

  const _handleShowAllReasons = () => setSExpandedRows( new Set( sExampleIdsWithReasons ) );

  const _handleHideAllReasons = () => setSExpandedRows( new Set() );

  const sExampleIdsWithReasons = useMemo( () => Object.entries( sExampleMetricDataMap )
    .filter( ( [ , m ] ) => sMetricKeys.some( k => m[ k ]?.result_status_reason || m[ k ]?.score_meta_data?.reason ) )
    .map( ( [ id ] ) => id ),
  [ sExampleMetricDataMap, sMetricKeys ] );

  const sHasAnyReasons = sExampleIdsWithReasons.length > 0;
  const sAllReasonsShown = sHasAnyReasons && sExampleIdsWithReasons.every( id => sExpandedRows.has( id ) );

  // Memoize table columns
  const tableColumns = useMemo<ColumnDef<Record<string, unknown>>[]>( () => {
    return [
      columnHelper.accessor( 'conversationId', {
        id: 'conversationId',
        header: 'Example ID',
        cell: info => { const val = info.getValue() as string; return <Tooltip content={ val } className={ styles.truncate }>{ truncate( info.getValue() as string, 50 ) }</Tooltip>; },
        maxSize: 200,
        enableSorting: true,
        enablePinning: true
      } ),
      columnHelper.display( {
        id: 'reasons',
        header: 'Reasons',
        maxSize: 40,
        cell: info => {
          const exampleId = info.row.original[ 'conversationId' ] as string;
          const metricMap = sExampleMetricDataMap[ exampleId ] ?? {};
          const hasAnyReason = sMetricKeys.some(
            k => metricMap[ k ]?.result_status_reason || metricMap[ k ]?.score_meta_data?.reason
          );
          if( !hasAnyReason ) return null;
          const isExpanded = sExpandedRows.has( exampleId );
          const CaretIcon = isExpanded ? PiCaretCircleUpBold : PiCaretCircleDownBold;
          return (
            <button
              type="button"
              className={ styles.icon_button }
              aria-label={ isExpanded ? 'Collapse reasons' : 'Expand reasons' }
              onClick={ () => _handleToggleRow( exampleId ) }
            >
              <CaretIcon aria-hidden="true" />
            </button>
          );
        }
      } ),
      columnHelper.display( {
        id: 'runExample',
        header: 'View Example',
        maxSize: 50,
        cell: info => {
          const exampleId = info.row.original[ 'conversationId' ] as string;
          if ( !sRun?.run.source_id ) {
            return null;
          }
          return (
            <button
              type="button"
              className={ styles.icon_button }
              aria-label="View run example"
              onClick={ () => {
                setSSelectedExampleId( exampleId );
                setSShowExampleModal( true );
              } }
            >
              <AiFillEye aria-hidden="true" />
            </button>
          );
        }
      } ),
      ...sMetricKeys.map( metricKey => {
        const [ providerId, metricName ] = metricKey.split( '|' );
        const metricLink = metricPageLink( providerId, resolveMetricVersion( sEvaluation, sMetricProviderVersions, providerId ) );
        const providerName = sMetricProviderNames[ providerId ];

        return columnHelper.accessor( metricKey, {
          id: metricKey,
          header: () => (
            <span className={ styles.column_header }>
              { metricLink
                ? <Link to={ metricLink } className={ styles.column_header_link } onClick={ ( e ) => e.stopPropagation() }>{ metricName }</Link>
                : metricName }
              { providerName && (
                <Tooltip content={ providerName } position="top">
                  <AiOutlineInfoCircle aria-hidden="true" className={ styles.column_header_info } />
                </Tooltip>
              ) }
            </span>
          ),
          cell: info => {
            const value = info.getValue();
            return ( value as { display?: unknown } )?.display ?? value;
          },
          sortingFn: ( rowA, rowB, columnId ) => {
            const a = rowA.getValue( columnId ) as { sortValue?: number; display?: unknown } | number | string;
            const b = rowB.getValue( columnId ) as { sortValue?: number; display?: unknown } | number | string;

            const aVal = typeof a === 'object' && a !== null ? ( a.sortValue ?? -Infinity ) : ( typeof a === 'number' ? a : parseFloat( String( a ) ) || -Infinity );
            const bVal = typeof b === 'object' && b !== null ? ( b.sortValue ?? -Infinity ) : ( typeof b === 'number' ? b : parseFloat( String( b ) ) || -Infinity );

            return aVal - bVal;
          },
          maxSize: 150,
          enableSorting: true,
          meta: { testId: `column-${ metricName }` }
        } );
      } )
    ];
  }, [ sMetricKeys, sExpandedRows, sExampleMetricDataMap, sEvaluation, sMetricProviderVersions, sMetricProviderNames ] );

  // Memoize table data
  const tableData = useMemo<Record<string, unknown>[]>( () => {
    const data: Record<string, unknown>[] = [];

    // sort examples alphabetically for consistency across renders for object keys
    const sortedExampleIdData = Object.entries( sExampleMetricDataMap )
      .sort( ( [ exampleIdA ], [ exampleIdB ] ) => exampleIdA.localeCompare( exampleIdB ) );

    // iterate over ordered examples
    for( const [ exampleId, result ] of sortedExampleIdData ) {
      const row: Record<string, unknown> = { conversationId: exampleId };
      const isRowExpanded = sExpandedRows.has( exampleId );

      // use ordered found metric keys
      for( const metricKey of sMetricKeys ) {
        const resultData = result[ metricKey ];
        const score: string = resultData?.score || '-';
        const isError = ERROR_RESULT_STATUSES.includes( ( resultData?.result_status || '' ).toLowerCase() );

        let displayScore: string;
        let numericScore: number = -Infinity;
        try { // try parsing score as a number
          const parsedScore: number = parseFloat( score );

          if( isNaN( parsedScore ) ) throw Error();

          numericScore = parsedScore;
          displayScore = parsedScore.toFixed( 3 );
        } catch( e ) { // score isn't a number display as is
          displayScore = score;
        }

        const reason = resultData?.result_status_reason || resultData?.score_meta_data?.reason;

        const scoreNode = isError
          ? <Badge variant="error" icon={ <GoAlertFill aria-hidden="true" /> }>Error</Badge>
          : <span className={ styles.result_score_value }>{ displayScore }</span>;

        row[ metricKey ] = {
          sortValue: isError ? -Infinity : numericScore,
          display: reason ? (
            <div className={ styles.metric_cell }>
              { scoreNode }
              { isRowExpanded && (
                <span className={ styles.inline_reason }>{ reason }</span>
              ) }
            </div>
          ) : scoreNode
        };
      }

      data.push( row );
    }

    return data;
  }, [ sExampleMetricDataMap, sMetricKeys, sExpandedRows ] );

  const hasLitellmError = useMemo( () => {
    // errors from both router and proxy layers of litellm, to distinguish between
    // model errors and other errors that fall under litellm.BadRequestError
    const modelErrorPatterns = [
      'no healthy deployments',
      'invalid model name passed in',
      'no \'model_name\' with this string',
      'llm provider not provided'
    ];
    return Object.values( sExampleMetricDataMap ).some( result => sMetricKeys.some( metricKey => {
      const reason = result[ metricKey ]?.result_status_reason?.toLowerCase();
      return reason?.includes( 'litellm.badrequesterror' ) &&
          modelErrorPatterns.some( pattern => reason.includes( pattern ) );
    } )
    );
  }, [ sExampleMetricDataMap, sMetricKeys ] );

  // Create table instance with column pinning, sorting, and pagination
  const resultsTable = useReactTable( {
    data: tableData,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableColumnPinning: true,
    state: {
      sorting,
      columnPinning
    },
    onSortingChange: setSorting,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 30
      }
    }
  } );

  const _handleRerun = async( includeReason: boolean ): Promise<void> => {
    if( !sRun?.run.source_id ) {
      toast.error( 'No source ID available for rerun' );
      return;
    }

    try {
      setSIsRerunning( true );
      await APIGateway.evals.evaluationRunCreate( qEvaluationId, {
        data_source: {
          data_origin: 'reference',
          source_id: sRun.run.source_id,
          source_version: sRun.run.source_version ?? 'latest'
        },
        include_reason: includeReason
      } );
      toast.success( 'Run created successfully' );
      setSShowRerunModal( false );
      navigate( `/evaluations/runs?eid=${ qEvaluationId }` );
    } catch( e ) {
      console.error( e );
      toast.error( formatRunCreateError( parseRunCreateError( e ).detail ) );
      setSIsRerunning( false );
    }
  };

  const hasSource = !!( sRun?.run.source_id && sSourceName );

  return (
    <div className={ clsx( 'page' ) }>
      <ToastContainer />
      {
        sIsLoading
          ? <Loader />
          : (
              <>
                {
                  sShowSourceModal && sSource && (
                    <ModalSourceShow
                      source={ sSource }
                      onCloseAction={ () => setSShowSourceModal( false ) }
                    />
                  )
                }
                {
                  sShowMetricModal && sSelectedMetricConfig && (
                    <ModalMetricShow
                      metricConfig={ sSelectedMetricConfig }
                      onCloseAction={ () => setSShowMetricModal( false ) }
                    />
                  )
                }
                {
                  sShowRerunModal && (
                    <ModalRunRerun
                      onClose={ () => setSShowRerunModal( false ) }
                      onConfirm={ _handleRerun }
                      sourceName={ sSourceName }
                      sourceVersion={ sRun?.run.source_version }
                      isLoading={ sIsRerunning }
                    />
                  )
                }
                {
                  sShowExampleModal && sSelectedExampleId && (
                    <ModalExampleShow
                      exampleId={ sSelectedExampleId }
                      source_id={ sRun?.run.source_id ?? '' }
                      source_version={ sRun?.run.source_version ?? 'latest' }
                      metricResults={ sExampleMetricDataMap[ sSelectedExampleId ] ?? {} }
                      metricKeys={ sMetricKeys }
                      metricProviderNames={ sMetricProviderNames }
                      onCloseAction={ () => {
                        setSShowExampleModal( false );
                        setSSelectedExampleId( null );
                      } }
                    />
                  )
                }
                <DetailPageHeader
                  backTo={ `/evaluations/runs?eid=${ qEvaluationId }` }
                  backLabel={ `Back to ${ sEvaluation?.name ?? 'Evaluation' }` }
                  backTestId="back-to-evaluation"
                  title={ sEvaluation ? (
                    <span className={ styles.title_row }>
                      <span>{ sEvaluation.name }</span>
                      <span className={ styles.run_title }>Run: { sRun?.run.id ?? '' }</span>
                    </span>
                  ) : '' }
                  titleSize="md"
                  divider
                  actions={
                    sRun?.run.source_id && (
                      <Button
                        variant='primary'
                        icon={ <Icon icon={ FiRefreshCw } aria-hidden="true" /> }
                        onClick={ () => setSShowRerunModal( true ) }
                        loading={ sIsRerunning }
                        disabled={ sIsRerunning }
                        title="Create a new run with the same source"
                        aria-label="Create a new run with the same source"
                      >
                        Rerun with Source
                      </Button>
                    )
                  }
                />
                <div className={ clsx( 'layout__row', 'pad', 'space_none', styles.run_meta ) }>
                  <span className={ styles.run_by } data-testid="run-by">
                    <span className={ styles.run_meta_label }>Run by:</span> { sRunBy }
                  </span>
                  <span className={ styles.run_status }>
                    <span className={ styles.run_meta_label }>Run Status:</span>
                    <StatusIcon status={ sRun?.run.status } />
                    <span className={ styles.run_status_text }>{ runStatusLabel( sRun?.run.status ) }</span>
                    {
                      sRun?.run.status_reason && (
                        <Tooltip content={ sRun.run.status_reason } position="right">
                          <AiOutlineInfoCircle aria-hidden="true" />
                        </Tooltip>
                      )
                    }
                    {
                      hasLitellmError && (
                        <span className={ styles.litellm_error }>This evaluation failed because a model it references does not exist. Update the evaluation metric to use a different model.</span>
                      )
                    }
                  </span>
                </div>
                <div className={ clsx( 'layout__row', 'pad', styles.cards_row ) }>
                  <SummaryCard
                    className={ styles.summary_card }
                    data-testid="evaluation-source-card"
                    label="Evaluation Source"
                    to={ hasSource ? `/evaluations/source?sid=${ sRun?.run.source_id }&version=${ sRun?.run.source_version ?? 'latest' }` : undefined }
                    value={ hasSource ? `${ sSourceName } (v${ sRun?.run.source_version ?? 'Latest' })` : 'Inline JSON' }
                    secondary={ sRun ? SOURCE_TYPE_DISPLAY_NAMES[ sRun.run.source_type ] : undefined }
                    actions={
                      hasSource && (
                        <Tooltip content="Preview source" position="top">
                          <button
                            type="button"
                            className={ styles.icon_button }
                            aria-label={ `Preview source: ${ sSourceName }` }
                            onClick={ () => setSShowSourceModal( true ) }
                          >
                            <AiFillEye aria-hidden="true" />
                          </button>
                        </Tooltip>
                      )
                    }
                  />
                  { runSchedule && (
                    <SummaryCard
                      className={ styles.summary_card }
                      data-testid="run-schedule-card"
                      label="Run Schedule"
                      value={ formatCronCadence( runSchedule.cron_expression ) }
                      secondary={ match( runSchedule.state )
                        .with( 'ACTIVE', () => (
                          <>
                            Next run:{ ' ' }
                            <TimeFormatter
                              sourceType={ TimeSourceTypeEnum.ISO }
                              value={ runSchedule.next_fire_at }
                              opts={ { useLocalZone } }
                              outputFormat={ OutputFormatEnum.STRING_SINGLE }
                            />
                          </>
                        ) )
                        .with( 'PAUSED', () => 'Schedule paused' )
                        .with( 'DISABLED', () => 'Schedule disabled' )
                        .exhaustive() }
                    />
                  ) }
                </div>
                { runProgress && (
                  <div className={ clsx( 'layout__row', 'pad', styles.progress_banner ) }>
                    <div className={ styles.progress_info }>
                      <span className={ styles.progress_dot } />
                      <span>Run in progress — { runProgress.sync_percent_complete }% complete</span>
                      <span className={ styles.progress_detail }>
                        ({ runProgress.sync_succeeded + runProgress.sync_failed } / { runProgress.total_sync_tasks } tasks)
                      </span>
                    </div>
                    <div className={ styles.progress_bar_container }>
                      <div
                        className={ styles.progress_bar }
                        style={ { width: `${ runProgress.sync_percent_complete }%` } }
                      />
                    </div>
                  </div>
                ) }
                <div className={ clsx( 'layout__row', 'pad', 'space_none', styles.layout__stats ) }>
                  <Stats
                    sMetricKeys={ sMetricKeys }
                    sMetricDataMap={ sMetricDataMap }
                    evaluation={ sEvaluation }
                    metricProviderNames={ sMetricProviderNames }
                    metricProviderVersions={ sMetricProviderVersions }
                    onShowMetricModal={ ( config ) => {
                      setSSelectedMetricConfig( config );
                      setSShowMetricModal( true );
                    } }
                  />
                </div>
                <div className={ clsx( 'layout__row', 'pad', styles.conversations_header ) }>
                  <Heading as="h2" size="md">Examples</Heading>
                  { sHasAnyReasons && (
                    <button
                      type="button"
                      className={ styles.reasons_toggle }
                      onClick={ sAllReasonsShown ? _handleHideAllReasons : _handleShowAllReasons }
                    >
                      <span>{ sAllReasonsShown ? 'Hide all run reasons' : 'Show all run reasons' }</span>
                      <Icon icon={ sAllReasonsShown ? PiCaretCircleUpBold : PiCaretCircleDownBold } aria-hidden="true" />
                    </button>
                  ) }
                </div>
                <div className={ styles.results_table }>
                  <NativeTable table={ resultsTable } tbodyTestId="examples-table" />
                </div>
              </>
            )
      }
    </div>
  );
}
